import React, { useEffect, useState } from "react";
import {
  Heart,
  Loader2,
  ChevronRight,
  MapPin,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Navbar from "./Navbar";
import Footer from "./Footer";
import useUserStore from "../store/userStore";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Favorite = {
  id: number;
  created_at: string;
  item_name: string;
  item_type: string;
};

type FavoritesResponse = {
  success: boolean;
  data: Favorite[];
  message: string;
};

const Favorites: React.FC = () => {
  const { user, token } = useUserStore();

  const navigate = useNavigate();

  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFavorites = async () => {
      if (!user?.userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${BASE_URL}/api/favorites/?user_id=${user.userId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",

              ...(token
                ? {
                    Authorization: `Bearer ${token}`,
                  }
                : {}),
            },
          }
        );

        if (!response.ok) {
          const errorText = await response.text();

          console.error(
            "Favorites API Error:",
            errorText
          );

          throw new Error(
            "Failed to fetch favorites"
          );
        }

        const result: FavoritesResponse =
          await response.json();

        console.log(
          "Favorites API Response:",
          result
        );

        setFavorites(result.data || []);
      } catch (error) {
        console.error(
          "Error fetching favorites:",
          error
        );

        setError(
          "دریافت علاقه‌مندی‌ها با خطا مواجه شد."
        );

        toast.error(
          "دریافت علاقه‌مندی‌ها با خطا مواجه شد",
          {
            className: "toast",
            hideProgressBar: true,
          }
        );
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [user?.userId, token]);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">

      <Navbar />

      <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-8">

          <button
            onClick={handleBack}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-800 transition"
          >
            <ChevronRight size={20} />

            <span className="font-myYekanMedium">
              بازگشت
            </span>
          </button>

          <div className="flex items-center gap-2">

            <Heart
              size={24}
              className="text-red-500"
              fill="currentColor"
            />

            <h1 className="text-xl font-myYekanBold text-gray-800">
              علاقه‌مندی‌ها
            </h1>

          </div>

        </div>

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">

            <Loader2
              size={32}
              className="animate-spin text-gray-500"
            />

            <p className="mt-4 text-gray-500 font-myYekanMedium">
              در حال دریافت علاقه‌مندی‌ها...
            </p>

          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-20">

            <Heart
              size={48}
              className="text-gray-300"
            />

            <p className="mt-4 text-red-500 font-myYekanMedium text-center">
              {error}
            </p>

            <button
              onClick={() =>
                window.location.reload()
              }
              className="mt-4 px-5 py-2 rounded-xl bg-gray-800 text-white font-myYekanMedium hover:bg-gray-700 transition"
            >
              تلاش مجدد
            </button>

          </div>
        )}

        {/* Empty State */}
        {!loading &&
          !error &&
          favorites.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20">

              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center">

                <Heart
                  size={40}
                  className="text-gray-400"
                />

              </div>

              <h2 className="mt-6 text-lg font-myYekanBold text-gray-700">
                هنوز علاقه‌مندی‌ای ندارید
              </h2>

              <p className="mt-2 text-sm text-gray-400 font-myYekanMedium text-center">
                مکان‌های مورد علاقه خود را ذخیره کنید
                تا بعداً به راحتی به آن‌ها دسترسی داشته باشید.
              </p>

              <button
                onClick={() => navigate("/")}
                className="mt-6 px-6 py-3 rounded-xl bg-gray-800 text-white font-myYekanMedium hover:bg-gray-700 transition"
              >
                مشاهده مکان‌ها
              </button>

            </div>
          )}

        {/* Favorites */}
        {!loading &&
          !error &&
          favorites.length > 0 && (
            <div>

              {/* Count */}
              <p className="mb-5 text-sm text-gray-500 font-myYekanMedium">
                {favorites.length} مکان ذخیره شده
              </p>

              {/* Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

                {favorites.map((favorite) => (
                  <div
                    key={favorite.id}
                    className="
                      bg-white
                      border
                      border-gray-100
                      rounded-2xl
                      p-5
                      shadow-sm
                      hover:shadow-md
                      transition
                    "
                  >

                    {/* Card Header */}
                    <div className="flex items-start justify-between gap-4">

                      <div className="flex items-start gap-3 min-w-0">

                        {/* Place Icon */}
                        <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-gray-100 flex items-center justify-center">

                          <MapPin
                            size={22}
                            className="text-gray-600"
                          />

                        </div>

                        {/* Name */}
                        <div className="min-w-0">

                          <h2 className="text-base font-myYekanBold text-gray-800 leading-7">
                            {favorite.item_name}
                          </h2>

                          <p className="mt-1 text-sm text-gray-500 font-myYekanMedium">
                            {favorite.item_type}
                          </p>

                        </div>

                      </div>

                      {/* Favorite Icon */}
                      <Heart
                        size={22}
                        className="flex-shrink-0 text-red-500"
                        fill="currentColor"
                      />

                    </div>

                    {/* Divider */}
                    <div className="my-4 border-t border-gray-100" />

                    {/* Bottom Section */}
                    <div className="flex items-center justify-between">

                      <span className="text-xs text-gray-400 font-myYekanMedium">
                        ذخیره شده در علاقه‌مندی‌ها
                      </span>

                      <span
                        className="text-xs text-gray-400 font-myYekanFaNumRegular"
                        dir="ltr"
                      >
                        {new Date(
                          favorite.created_at
                        ).toLocaleDateString("fa-IR")}
                      </span>

                    </div>

                  </div>
                ))}

              </div>

            </div>
          )}

      </main>

      <Footer />

    </div>
  );
};

export default Favorites;